/** The three surfaces §82 finishes cover (kept in step with engine/finishes.ts). */
export type StandardSurface = "floor" | "walls" | "ceiling";

/**
 * A RENOVATION STANDARD (spec §90) — "how I normally renovate a property".
 *
 * It holds only CHOICES the company repeats: the finishes it installs, the
 * room package it uses for a kind of room, the assembly (and optionally the
 * product) that specifies a kind of work, and the estimating profile it
 * prices with. It is deliberately NOT a second scope truth:
 *
 *   applying a standard writes the ORDINARY rows §82, §10, §4 and §77
 *   already own, and the project is editable everywhere afterwards.
 *
 * PRECEDENCE, which extends §82's rule rather than replacing it:
 *
 *   explicit room / work-item override
 *     > a project-specific choice (the project's own finish default, the
 *       assembly somebody attached, a typed quantity)
 *     > the PROJECT'S SNAPSHOT of its renovation standard
 *     > room-type suggestion
 *     > nothing (say so; never invent)
 *
 * The standard sits below the project because applying it WRITES project
 * rows: from then on the project's own rows are what the estimate reads,
 * which is what makes tomorrow's edit to the standard harmless.
 */
export interface RenovationStandard {
  id: string;
  orgId: string;
  name: string;
  notes: string;
  /** §77 profile to price with — referenced, never copied. */
  estimatingProfileId: string | null;
  isDefault: boolean;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface StandardFinishChoice {
  surface: StandardSurface;
  materialKey: string;
}

export interface StandardSpec {
  featureDefinitionId: string;
  /** The assembly that prices this work ("Install LVP") — the specification. */
  assemblyTemplateId: string | null;
  /** The product the company actually buys, when it is stable enough to be a standard. */
  productId: string | null;
  notes: string;
}

export interface StandardRoomPackage {
  roomTypeId: string;
  packageId: string;
}

export interface RenovationStandardDetail {
  standard: RenovationStandard;
  finishes: StandardFinishChoice[];
  specs: StandardSpec[];
  roomPackages: StandardRoomPackage[];
}

/**
 * What a project froze when it was created from a standard. Stored as JSON
 * on the project beside the standard's id and name, so the project can
 * always say what it started from even after the standard changes, is
 * archived or is deleted.
 */
export interface ProjectStandardSnapshot {
  /** Explicit §92 standard application opts into automatic finish reconciliation. */
  automaticFinishVersion?: 1;
  standardId: string;
  name: string;
  appliedAt: string;
  estimatingProfileId: string | null;
  finishes: StandardFinishChoice[];
  specs: StandardSpec[];
  roomPackages: StandardRoomPackage[];
}

export function standardSnapshotOf(detail: RenovationStandardDetail, appliedAt: string): ProjectStandardSnapshot {
  return {
    standardId: detail.standard.id,
    name: detail.standard.name,
    appliedAt,
    estimatingProfileId: detail.standard.estimatingProfileId,
    finishes: detail.finishes.map((f) => ({ ...f })),
    specs: detail.specs.map((s) => ({ ...s })),
    roomPackages: detail.roomPackages.map((r) => ({ ...r })),
  };
}

/** The package this project's standard uses for a room type (null = none). */
export function packageForRoomType(snapshot: ProjectStandardSnapshot | null, roomTypeId: string | null): string | null {
  if (!snapshot || !roomTypeId) return null;
  return snapshot.roomPackages.find((r) => r.roomTypeId === roomTypeId)?.packageId ?? null;
}

/** The specification this project's standard uses for a kind of work. */
export function specForWork(snapshot: ProjectStandardSnapshot | null, featureDefinitionId: string | null): StandardSpec | null {
  if (!snapshot || !featureDefinitionId) return null;
  return snapshot.specs.find((s) => s.featureDefinitionId === featureDefinitionId) ?? null;
}

export function parseStandardSnapshot(json: string | null): ProjectStandardSnapshot | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as ProjectStandardSnapshot;
    if (!parsed || typeof parsed !== "object" || typeof parsed.standardId !== "string") return null;
    return {
      standardId: parsed.standardId,
      ...(parsed.automaticFinishVersion === 1 ? { automaticFinishVersion: 1 as const } : {}),
      name: typeof parsed.name === "string" ? parsed.name : "",
      appliedAt: typeof parsed.appliedAt === "string" ? parsed.appliedAt : "",
      estimatingProfileId: parsed.estimatingProfileId ?? null,
      finishes: Array.isArray(parsed.finishes) ? parsed.finishes : [],
      specs: Array.isArray(parsed.specs) ? parsed.specs : [],
      roomPackages: Array.isArray(parsed.roomPackages) ? parsed.roomPackages : [],
    };
  } catch {
    return null;
  }
}
